import { useEffect, useRef, useState } from "react";
import { ListPlus, Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18n";

/**
 * Adds a track to one of the listener's playlists, or to a new one.
 *
 * Used from the player and from the queue, so the menu positions itself above
 * the button: both live at the bottom of the screen.
 */
export default function AddToPlaylist({ trackId, className = "", align = "right" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [added, setAdded] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/music/playlists").then((r) => setPlaylists(r.data.playlists)).catch(() => setPlaylists([]));

    const onAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const addTo = async (playlistId) => {
    try {
      await api.post(`/music/playlists/${playlistId}/tracks`, { track_id: trackId });
      setAdded(playlistId);
      toast.success(t("added_to_playlist"));
      setTimeout(() => setOpen(false), 700);
      window.dispatchEvent(new Event("playlists-changed"));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/music/playlists", { name: name.trim() });
      await api.post(`/music/playlists/${data.id}/tracks`, { track_id: trackId });
      toast.success(t("added_to_playlist"));
      setName("");
      setOpen(false);
      window.dispatchEvent(new Event("playlists-changed"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        data-testid="add-to-playlist-btn"
        onClick={() => setOpen((v) => !v)}
        title={t("add_to_playlist")}
        className={`p-2 rounded-md transition-colors ${open ? "text-[#FF2A4D] bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
      >
        <ListPlus className="w-5 h-5" />
      </button>

      {open && (
        <div
          data-testid="add-to-playlist-menu"
          className={`absolute bottom-full mb-2 ${align === "right" ? "right-0" : "left-0"} w-64 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl p-2 z-[70]`}
        >
          <p className="text-xs text-zinc-500 px-2 py-1">{t("add_to_playlist")}</p>

          <div className="max-h-56 overflow-y-auto">
            {playlists === null ? (
              <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600" /></div>
            ) : playlists.length === 0 ? (
              <p className="text-xs text-zinc-600 px-2 py-2">{t("no_playlists_yet")}</p>
            ) : (
              playlists.map((pl) => (
                <button
                  key={pl.id}
                  onClick={() => addTo(pl.id)}
                  data-testid={`add-to-${pl.id}`}
                  className="w-full text-left px-2 py-2 rounded-lg hover:bg-white/10 text-sm flex items-center justify-between gap-2"
                >
                  <span className="truncate">{pl.name}</span>
                  {added === pl.id ? (
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                  ) : (
                    <span className="text-xs text-zinc-600 shrink-0">{pl.track_count}</span>
                  )}
                </button>
              ))
            )}
          </div>

          <form onSubmit={createAndAdd} className="flex gap-1.5 mt-2 pt-2 border-t border-white/10">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("playlist_name_ph")}
              maxLength={80}
              className="flex-1 min-w-0 bg-[#050505] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF2A4D]"
            />
            <button
              type="submit"
              disabled={!name.trim() || creating}
              title={t("create")}
              className="bg-[#FF2A4D] hover:bg-[#E61C3D] disabled:opacity-50 rounded-lg px-2 shrink-0"
            >
              <Plus className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
